import React from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter } from 'react-router-dom';
import { ApolloClient, ApolloLink } from 'apollo-boost';
import { fromPromise } from 'apollo-link';
import { onError } from 'apollo-link-error';
import { InMemoryCache } from 'apollo-cache-inmemory';
import { ApolloProvider } from '@apollo/react-hooks';
import { createUploadLink } from 'apollo-upload-client';
import firebase from 'firebase/app';
import { FirebaseDatabaseProvider } from '@react-firebase/database';

import ScrollToTop from './scroll_to_top';
import './styles/common.css';
import './styles/app.css';
import config from './config';
import Checker from './core/Checker';
import AppGate from './core/AppGate';
import { AuthProvider } from './context/AuthContext';
import { getAccessToken } from './utils/accessTokenStore';
import refreshAccessToken from './utils/refreshAccessToken';
import logout from './utils/logout';
import 'firebase/auth';
import 'firebase/database';

const { API_URL, FIREBASE_CONFIG: firebaseConfig } = config;

const httpLink = createUploadLink({ uri: API_URL });
const authLink = new ApolloLink((operation, forward) => {
  const token = getAccessToken();

  operation.setContext({
    headers: {
      authorization: token ? `Bearer ${token}` : '',
      'x-app-version': '3.2.0',
      'x-source': 'WEB',
    },
  });

  return forward(operation);
});

// On an expired/invalid access token, refresh once and retry the operation. `authLink` always
// re-reads the in-memory access token on every pass, so once refreshAccessToken() has updated
// it, forwarding the operation back through authLink picks it up with no extra wiring here.
// `hasRetriedAuth` caps this at one retry per operation — otherwise a UNAUTHENTICATED that ISN'T
// a token-expiry (e.g. a genuine permission error, which reuses the same error code) would retry
// forever against a token that refreshes fine but is still not permitted.
const errorLink = onError(({ graphQLErrors, operation, forward }) => {
  if (!graphQLErrors || !graphQLErrors.some((err) => err.code === 'UNAUTHENTICATED')) {
    return undefined;
  }

  if (operation.getContext().hasRetriedAuth) {
    return undefined;
  }

  // The token THIS request actually failed with — read from the operation's own context, not
  // freshly from the token store. By the time this runs, another operation's refresh may have
  // already updated it, in which case a fresh read here would just equal the current
  // (already-refreshed) value, making the "did someone else already refresh?" check in
  // refreshAccessToken() always false and triggering a redundant second refresh call.
  const staleAccessToken = operation.getContext().headers?.authorization?.replace('Bearer ', '');

  return fromPromise(
    refreshAccessToken(staleAccessToken)
      .then(() => {
        operation.setContext({ hasRetriedAuth: true });
      })
      .catch((err) => {
        logout();
        throw err;
      }),
  ).flatMap(() => forward(operation));
});

// Mirrors a logout that happened in another tab: that tab clears @refresh_token as part of
// logout(), and the storage event only fires in OTHER tabs, which is exactly who needs to react.
// The access token itself is never persisted, so @refresh_token is the only durable, cross-tab
// signal that a logout happened.
window.addEventListener('storage', (event) => {
  if (event.key === '@refresh_token' && event.newValue === null) {
    window.location.reload(true);
  }
});

const client = new ApolloClient({
  link: errorLink.concat(authLink).concat(httpLink),
  cache: new InMemoryCache(),
});

const component = (
  <ApolloProvider client={client}>
    {/* eslint-disable-next-line react/jsx-props-no-spreading */}
    <FirebaseDatabaseProvider firebase={firebase} {...firebaseConfig}>
      <BrowserRouter>
        <ScrollToTop>
          <Checker>
            <AuthProvider>
              <AppGate />
            </AuthProvider>
          </Checker>
        </ScrollToTop>
      </BrowserRouter>
    </FirebaseDatabaseProvider>
  </ApolloProvider>
);

ReactDOM.render(component, document.getElementById('root'));
